/** The two money flows that meet on a single customer, per month.
 *
 * Chawowa charges the tenant a per-customer fee plus a rate for every minute
 * spoken. The tenant charges their customer one flat monthly price. Profit is
 * what's left, but the minute cost only lands if the customer actually calls —
 * so it is a range, and `profit` reports the safe end (every included minute
 * used). Pricing off the fee alone is how a plan quietly loses money.
 *
 * Everything is in cents. The Chawowa side comes live from
 * `GET /customers/billing_preview/`; nothing here may assume a rate.
 */
export type PlanEconomics = {
  /** What the customer pays the tenant. */
  price: number;
  /** Chawowa's per-customer fee. */
  fee: number;
  /** The included minutes at Chawowa's rate — owed only if they are used. */
  minutesCost: number;
  /** fee + minutesCost: the most one customer can cost in a month. */
  cost: number;
  /** price − cost, assuming every included minute is used. */
  profit: number;
  /** price − fee: the best case, a customer who never calls. */
  profitIfUnused: number;
  /** The lowest monthly price that still covers a fully-used plan. */
  breakEven: number;
};

export function planEconomics(args: {
  priceCents: number;
  includedMinutes: number;
  feeCents: number;
  costPerMinuteCents: number;
}): PlanEconomics {
  const { priceCents, includedMinutes, feeCents, costPerMinuteCents } = args;
  const minutesCost = includedMinutes * costPerMinuteCents;
  const cost = feeCents + minutesCost;
  return {
    price: priceCents,
    fee: feeCents,
    minutesCost,
    cost,
    profit: priceCents - cost,
    profitIfUnused: priceCents - feeCents,
    breakEven: cost,
  };
}
